/**
 * Frontend mirror of the backend ANZSIC taxonomy.
 * Canonical source: /app/backend/config/industries_taxonomy.py
 *   API: GET /api/industry-taxonomy
 *
 * This static file is for SSR + initial paint. The runtime hook
 * `useIndustryTaxonomy()` fetches the authoritative list from the API
 * and overrides these defaults once mounted.
 *
 * IMPORTANT: when changing anything here, also update the backend taxonomy
 *            and run `pytest backend/tests/test_industries_taxonomy.py`.
 */

// 19 ANZSIC 2006 divisions. Keep in sync with backend ANZSIC_INDUSTRIES.
export const ANZSIC_DIVISIONS = [
  { id: 'agriculture',         division: 'A', name: 'Agriculture, Forestry & Fishing',      plural_name: 'Agriculture',       icon: 'wheat',         color: 'green',   accent_hex: '#16A34A' },
  { id: 'mining',              division: 'B', name: 'Mining',                                 plural_name: 'Mining',            icon: 'pickaxe',       color: 'stone',   accent_hex: '#78716C' },
  { id: 'manufacturing',       division: 'C', name: 'Manufacturing',                          plural_name: 'Manufacturing',     icon: 'factory',       color: 'slate',   accent_hex: '#475569' },
  { id: 'utilities',           division: 'D', name: 'Electricity, Gas, Water & Waste',        plural_name: 'Utilities',         icon: 'zap',           color: 'yellow',  accent_hex: '#CA8A04' },
  { id: 'construction',        division: 'E', name: 'Construction',                           plural_name: 'Construction',      icon: 'hard-hat',      color: 'amber',   accent_hex: '#D97706' },
  { id: 'wholesale',           division: 'F', name: 'Wholesale Trade',                        plural_name: 'Wholesale',         icon: 'warehouse',     color: 'blue',    accent_hex: '#2563EB' },
  { id: 'retail',              division: 'G', name: 'Retail Trade',                           plural_name: 'Retail',            icon: 'shopping-bag',  color: 'pink',    accent_hex: '#DB2777' },
  { id: 'hospitality',         division: 'H', name: 'Accommodation & Food Services',          plural_name: 'Hospitality',       icon: 'utensils',      color: 'orange',  accent_hex: '#EA580C' },
  { id: 'transport_logistics', division: 'I', name: 'Transport, Postal & Warehousing',        plural_name: 'Transport & Logistics', icon: 'truck',     color: 'cyan',    accent_hex: '#0891B2' },
  { id: 'media_telecom',       division: 'J', name: 'Information Media & Telecommunications', plural_name: 'Media & Technology', icon: 'tv',            color: 'indigo',  accent_hex: '#4F46E5' },
  { id: 'finance',             division: 'K', name: 'Financial & Insurance Services',         plural_name: 'Finance & Insurance', icon: 'wallet',       color: 'emerald', accent_hex: '#059669' },
  { id: 'real_estate',         division: 'L', name: 'Rental, Hiring & Real Estate',           plural_name: 'Real Estate',       icon: 'home',          color: 'red',     accent_hex: '#DC2626' },
  { id: 'professional',        division: 'M', name: 'Professional, Scientific & Technical',   plural_name: 'Professional Services', icon: 'briefcase',   color: 'violet',  accent_hex: '#7C3AED' },
  { id: 'admin_services',      division: 'N', name: 'Administrative & Support Services',      plural_name: 'Business Support',  icon: 'clipboard-list',color: 'teal',    accent_hex: '#0D9488' },
  { id: 'public_admin',        division: 'O', name: 'Public Administration & Safety',         plural_name: 'Public Sector',     icon: 'landmark',      color: 'zinc',    accent_hex: '#52525B' },
  { id: 'education',           division: 'P', name: 'Education & Training',                   plural_name: 'Education',         icon: 'graduation-cap',color: 'sky',     accent_hex: '#0284C7' },
  { id: 'healthcare',          division: 'Q', name: 'Health Care & Social Assistance',        plural_name: 'Healthcare',        icon: 'heart-pulse',   color: 'rose',    accent_hex: '#E11D48' },
  { id: 'arts_recreation',     division: 'R', name: 'Arts & Recreation Services',             plural_name: 'Arts & Recreation', icon: 'dumbbell',      color: 'purple',  accent_hex: '#9333EA' },
  { id: 'other_services',      division: 'S', name: 'Other Services',                         plural_name: 'Other Services',    icon: 'sparkles',      color: 'fuchsia', accent_hex: '#C026D3' },
];

// Legacy → canonical key mapper. MUST mirror backend INDUSTRY_ALIASES.
export const INDUSTRY_ALIASES = {
  property:             'real_estate',
  trades:               'other_services',
  home_services:        'other_services',
  logistics:            'transport_logistics',
  transport:            'transport_logistics',
  marine:               'transport_logistics',
  technology:           'media_telecom',
  it_services:          'media_telecom',
  saas:                 'media_telecom',
  software:             'media_telecom',
  media:                'media_telecom',
  beauty:               'other_services',
  fitness:              'arts_recreation',
  events:               'arts_recreation',
  entertainment:        'arts_recreation',
  veterinary:           'professional',
  legal:                'professional',
  accounting:           'professional',
  childcare:            'healthcare',
  automotive:           'other_services',
  nonprofit:            'other_services',
  government:           'public_admin',
  qsr:                  'hospitality',
  tourism:              'hospitality',
  // dropped entirely
  crm:                  null,
  corvana_tech:         null,
};

const CANON_SET = new Set(ANZSIC_DIVISIONS.map(d => d.id));

// Sub-vertical → parent division map (mirrors backend SUB_VERTICALS).
// Keep this in sync with backend config/industries_taxonomy.py::SUB_VERTICALS.
const SUB_VERTICAL_PARENT = {
  // Agriculture
  crop_production: 'agriculture', livestock: 'agriculture', aquaculture: 'agriculture', forestry: 'agriculture', viticulture: 'agriculture',
  // Mining
  metal_ore: 'mining', coal: 'mining', oil_gas: 'mining', mining_services: 'mining', quarrying: 'mining',
  // Manufacturing
  food_beverage: 'manufacturing', machinery: 'manufacturing', textiles: 'manufacturing', chemicals: 'manufacturing', metal_products: 'manufacturing', wood_paper: 'manufacturing',
  // Utilities
  electricity: 'utilities', gas: 'utilities', water_sewerage: 'utilities', waste_recycling: 'utilities',
  // Construction
  residential: 'construction', commercial: 'construction', civil: 'construction', construction_services: 'construction',
  // Wholesale
  food_wholesale: 'wholesale', machinery_wholesale: 'wholesale', motor_vehicle_wholesale: 'wholesale', other_wholesale: 'wholesale',
  // Retail
  supermarket_grocery: 'retail', fashion_apparel: 'retail', electronics: 'retail', homewares: 'retail', pharmacy: 'retail', ecommerce_pureplay: 'retail',
  // Hospitality
  cafe_restaurant: 'hospitality', qsr_fastfood: 'hospitality', pubs_bars: 'hospitality', accommodation: 'hospitality', catering: 'hospitality', clubs_gaming: 'hospitality',
  // Transport & Logistics
  road_freight: 'transport_logistics', rail_freight: 'transport_logistics', air_transport: 'transport_logistics', marine_shipping: 'transport_logistics', warehousing: 'transport_logistics', postal_courier: 'transport_logistics',
  // Media & Telecom
  software_saas: 'media_telecom', telecommunications: 'media_telecom', broadcasting: 'media_telecom', publishing: 'media_telecom', data_services: 'media_telecom',
  // Finance
  banking: 'finance', financial_advice: 'finance', insurance: 'finance', super_smsf: 'finance', mortgage_broking: 'finance',
  // Real Estate
  residential_sales: 'real_estate', commercial_sales: 'real_estate', property_management: 'real_estate', auctioneering: 'real_estate',
  // Professional
  legal: 'professional', accounting: 'professional', management_consulting: 'professional', architecture_engineering: 'professional', advertising_marketing: 'professional', veterinary: 'professional', scientific_rd: 'professional',
  // Admin services
  employment_services: 'admin_services', travel_agency: 'admin_services', cleaning: 'admin_services', security: 'admin_services', call_centres: 'admin_services',
  // Public admin
  government_services: 'public_admin', defence: 'public_admin', public_safety: 'public_admin', regulatory: 'public_admin',
  // Education
  schools: 'education', tertiary: 'education', vet_rto: 'education', tutoring: 'education', corporate_training: 'education',
  // Healthcare
  gp_clinic: 'healthcare', specialist: 'healthcare', allied_health: 'healthcare', dental: 'healthcare', aged_care: 'healthcare', childcare: 'healthcare', hospital_pathology: 'healthcare',
  // Arts & Recreation
  fitness_gyms: 'arts_recreation', sports_clubs: 'arts_recreation', events: 'arts_recreation', museums_galleries: 'arts_recreation', entertainment: 'arts_recreation', creative_arts: 'arts_recreation',
  // Other Services
  beauty_personal_care: 'other_services', automotive_repair: 'other_services', home_services: 'other_services', nonprofit: 'other_services', religious_civic: 'other_services',
};

/**
 * Map any legacy/alias/canonical/sub-vertical industry key to its canonical
 * ANZSIC division id. Returns `null` for dropped keys or unknown inputs.
 */
export function canonicalIndustry(raw) {
  if (!raw) return null;
  const key = String(raw).trim().toLowerCase();
  if (CANON_SET.has(key)) return key;
  if (key in INDUSTRY_ALIASES) return INDUSTRY_ALIASES[key];  // may be null
  if (key in SUB_VERTICAL_PARENT) return SUB_VERTICAL_PARENT[key];
  return null;
}

/**
 * Division metadata for a canonical (or legacy) industry key.
 * Returns `null` if unmappable.
 */
export function divisionFor(raw) {
  const canon = canonicalIndustry(raw);
  if (!canon) return null;
  return ANZSIC_DIVISIONS.find(d => d.id === canon) || null;
}

/** List of canonical industry ids (19 entries). */
export const CANONICAL_INDUSTRY_IDS = ANZSIC_DIVISIONS.map(d => d.id);
